import { useSyncExternalStore } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { ExternalLink } from 'lucide-react'

import styles from './Privacy.module.scss'

import EnterpriseTag from '@/components/EnterpriseTag'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectPlanType } from '@/store/features/user/userSlice'
import { PlanType } from '@/types'

interface PrivacyCardProps {
  title: string
  content: string
  link: string
  needsEnterprise?: boolean
}

const PrivacyCard = (props: PrivacyCardProps) => {
  const { title, content, link, needsEnterprise } = props

  return (
    <a
      className={styles.card}
      href={!needsEnterprise ? link : undefined}
      target="_blank"
      rel="noreferrer"
      aria-disabled={needsEnterprise}
    >
      <div className={styles.cardTitle}>
        {title}
        {needsEnterprise ? (
          <EnterpriseTag tooltipText={`Upgrade to ${PlanType.Enterprise} plan to access`} />
        ) : (
          <ExternalLink size={14} />
        )}
      </div>
      <div className={styles.cardContent}>{content}</div>
    </a>
  )
}

const content = [
  {
    title: 'Privacy policy',
    content: 'Our privacy policy explains how we collect, protect, and use your personal data.',
    link: 'https://www.wisprflow.ai/privacy-policy',
  },
  {
    title: 'Terms of service',
    content: 'Our terms of service outlines the terms you agree to when using our services.',
    link: 'https://www.wisprflow.ai/terms-of-service',
  },
  {
    title: 'Data controls',
    content: 'Our data controls page explains how we handle data.',
    link: 'https://www.wisprflow.ai/data-usage',
  },
  {
    title: 'Data processing addendum',
    content: 'Our data processing addendum outlines how we process data on your behalf.',
    link: 'https://drive.google.com/file/d/1l3nSm_AcFMAWfXfUegvaxG7P-uSj6A6X/view?usp=drive_link',
  },
]

const enterpriseContent = [
  {
    title: 'SOC 2 and HIPAA compliance',
    content: 'Our SOC 2 and HIPAA compliance ensures that we meet the highest standards of data security and privacy.',
    link: 'https://trust.wispr.ai',
  },
]

const Privacy = () => {
  const planType = useSelector(selectPlanType)
  // View as Admin hides upgrade-only cards instead of showing them locked.
  const hideEnterpriseCards =
    useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null && planType !== PlanType.Enterprise

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Privacy</title>
      </Helmet>
      <div className={`${styles.container} ${styles.privacyContainer}`}>
        <div className={styles.pageTitle}>
          <h6>Privacy</h6>
        </div>
        <div className={styles.cardContainer}>
          {content.map((item, index) => (
            <PrivacyCard key={index} title={item.title} content={item.content} link={item.link} />
          ))}
          {!hideEnterpriseCards &&
            enterpriseContent.map((item, index) => (
              <PrivacyCard
                key={index}
                title={item.title}
                content={item.content}
                link={item.link}
                needsEnterprise={planType !== PlanType.Enterprise}
              />
            ))}
        </div>
      </div>
    </>
  )
}

export default Privacy
